/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import type {
  AuthenticationMethodDescription,
  InputDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { Paragraph, Text } from "@smartsuite/react-ui/lib";
import { observer } from "mobx-react-lite";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { StyledContainerWrapper } from "../../../style";
import { StyledReadOnlySection } from "./style";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../../../../../types/candidate";
import { useRootStore } from "../../../../../../../../stores/hooks";
import { useEngineClient } from "../../../../../../../../services/engineClient";
import { useEffect, useState } from "react";
import type { ParagonData } from "../../../../../../../../services/paragon";
import { getParagon } from "../../../../../../../../services/paragon";
import { updateActionDescription } from "../../../../../../../../services/updateDescription";
import { getIntegrationLogo } from "../../../../../../../../utils/integration-logo";
import OpenInNew from "../../../../../../../../../generated/images/OpenInNew";
import { t } from "i18next";
import { translateIfPresent } from "../../../../../../../../locales/i18n";
import { Colors } from "@smartsuite/types";

export const ReadOnlyEditor = observer<{
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  integrations: IntegrationDescription[];
  showValidationError: boolean;
  action?: ActionCandidate;
  onClickTrash?: () => void;
  description?: InputDescription;
}>(
  ({
    input,
    onChange,
    integrations,
    action,
    description,
    onClickTrash,
    showValidationError,
  }) => {
    const store = useRootStore();
    const engineClient = useEngineClient();

    const [paragonData, setParagonData] = useState<ParagonData | undefined>(
      undefined,
    );
    const [value, setValue] = useState<string | undefined>(undefined);
    const [title, setTitle] = useState<string | undefined>(undefined);
    const [urlToOpen, setUrlToOpen] = useState<string | undefined>(undefined);
    const [isOnClick, setIsOnClick] = useState<boolean>(false);
    const [copied, setCopied] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string | undefined>(
      undefined,
    );

    const {
      automations: { candidate },
      integrations: { list },
    } = store;

    const integration = integrations.find((i: IntegrationDescription) => {
      return i.integrationId === action?.actionReference?.integrationId;
    });
    const authMethod = integration?.authenticationMethods.find(
      (a: AuthenticationMethodDescription) => {
        return a.authenticationMethodId === "paragon-auth";
      },
    );
    const credential = store.credentials.list.find((c) => {
      return c.credentialId === action?.credentialId;
    });

    const userId = credential?.metadata?.metadata.userId;

    useEffect(() => {
      if (showValidationError) {
        if (value === "") {
          setValidationError(t("field-editor.readonly-error"));
        }
      } else {
        setValidationError(undefined);
      }
    }, [input, description, showValidationError]);

    useEffect(() => {
      let copiedResetId: ReturnType<typeof setTimeout> | undefined;
      if (authMethod?.type.oneofKind === "paragonAuth" && isOnClick) {
        store.automations.setLoadingDynamicDescription(true);
        store.automations.addToLoadingDynamicDescriptionProcess(
          "action",
          action?.actionReference?.instanceId,
        );
        void getParagon(
          authMethod.type.paragonAuth.authenticateBrowserUrl,
          userId,
        )
          .then((data) => {
            setParagonData(data);
            store.automations.setLoadingDynamicDescription(false);
            store.automations.removeFromLoadingDynamicDescriptionProcess(
              "action",
              action?.actionReference?.instanceId,
            );
          })
          .finally(() => {
            store.automations.setRefreshingList(false);
          });
      } else {
        if (isOnClick && value) {
          // eslint-disable-next-line promise/catch-or-return
          navigator.clipboard
            .writeText(value)
            .then(() => {
              setIsOnClick(false);
              setCopied(true);
            })
            .catch((_) => {
              setCopied(false);
            })
            .finally(() => {
              copiedResetId = setTimeout(() => {
                setCopied(false);
              }, 2000);
            });
        }
      }
      return () => clearTimeout(copiedResetId);
    }, [isOnClick]);

    useEffect(() => {
      if (paragonData !== undefined) {
        // @ts-expect-error paragon external lib
        paragonData.client.connect(integration?.integrationId, {
          overrideRedirectUrl: paragonData.paragonRedirectOauthUrl,
          onClose: () => {
            setTimeout(() => {
              if (action && action.credentialId !== "") {
                store.automations.setLoadingDynamicDescription(true);
                store.automations.addToLoadingDynamicDescriptionProcess(
                  "action",
                  action.actionReference?.instanceId,
                );
                // eslint-disable-next-line promise/no-nesting
                void updateActionDescription(
                  candidate,
                  action,
                  list,
                  engineClient,
                  store.automations,
                )
                  .then(() => {
                    store.automations.setLoadingDynamicDescription(false);
                    store.automations.removeFromLoadingDynamicDescriptionProcess(
                      "action",
                      action?.actionReference?.instanceId,
                    );
                    setIsOnClick(false);
                    setParagonData(undefined);
                  })
                  .finally(() => {
                    store.automations.setRefreshingList(false);
                  });
              }
            }, 500);
          },
        });
      }
    }, [paragonData]);

    useEffect(() => {
      if (description?.editor?.editor.oneofKind === "readOnly") {
        setValue(description?.editor.editor.readOnly.value);
        setTitle(description?.editor.editor.readOnly.label);
        setUrlToOpen(description?.editor.editor.readOnly.urlToOpen);
      }
    }, [description]);

    useEffect(() => {
      if (value !== undefined) {
        onChange({
          ...input,
          value: {
            values: [
              {
                value: {
                  oneofKind: "string",
                  string: value,
                },
              },
            ],
          },
        });
      }
    }, [value]);

    const onClick = (): void => {
      setIsOnClick(true);
    };

    const onClickUrlToOpen = (e: React.MouseEvent): void => {
      e.stopPropagation();
    };

    const IconIntegration = getIntegrationLogo(
      integration?.integrationId ?? "",
    );

    return (
      <StyledContainerWrapper>
        {description !== undefined && (
          <CaptionBlock description={description} onClickTrash={onClickTrash} />
        )}
        <StyledReadOnlySection onClick={() => onClick()}>
          <Text tag="div">
            <IconIntegration />
            <span className="text--ellipsis">
              {translateIfPresent(
                `readonly-editor.${title ?? ""}`,
                title ?? "",
              )}
            </span>
            {copied && (
              <span style={{ color: Colors.bGreen }}>
                {translateIfPresent(`readonly-editor.copied`, "Copied")}
              </span>
            )}
            {urlToOpen && (
              <a
                href={urlToOpen}
                target="_blank"
                onClick={(e) => onClickUrlToOpen(e)}
                rel="noopener"
              >
                <OpenInNew />
              </a>
            )}
          </Text>
        </StyledReadOnlySection>
        {validationError !== undefined && (
          <Paragraph color={"error"} size={"xs"}>
            {validationError}
          </Paragraph>
        )}
      </StyledContainerWrapper>
    );
  },
);
